// dependencies
import React from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { Map, List } from "immutable";
import { connect } from "react-redux";

// components
import Select from "react-select";
import User from "Components/User";
import Button from "Components/Button";
import TextInput from "Components/Inputs/Text";
import CompanyLogo from "Components/CompanyLogo";
import Spinner from "Components/Spinner/PageSpinner";
import CompanyNavigation from "./Components/Navigation";
import CompanySetting from "./Components/CompanySetting";
import CompanySnapshot from "./Components/CompanySnapshot";
import CompanyInvoice from "./Components/CompanyInvoice";
import DeletedObjects from "./Components/DeletedObjects";
import CompanyFeatureFlags from "./Components/FeatureFlags";
import EditFeatureFlag from "./Components/EditFeatureFlag";
import AccessControls from "./Components/access-controls";
import Modal from "Components/Modal";
import BulkEmailChange from "./Components/BulkEmailChange";
import EngagementLogs from "./Components/EngagementLogs";

// actions
import { changePageUI as updateUI } from "Actions/ui";
import {
	init,
	fetchPageData,
	changeQuery,
	sendIntegrationUpdate,
	deleteIntegration,
	resetIntegration,
	resetNotificationDefaults,
	exportData,
	copyStagingData,
	runReportingEtls,
	sendSurveyManually,
	sendReminderEmailsManually,
} from "./actions";

// selectors
import { getIsRequesting, getCurrentEnvironment, getUserRoles, getUserIsAdmin } from "Selectors/app";
import {
	getCompanyUsers,
	getCurrentQuery,
	getCompany,
	getCompanyID,
	getPageLimit,
	getPageSize,
	getCurrentPage,
	getData,
	getIntegrationValue,
	getOriginalIntegrationValue,
	getInitalized,
	getLastSyncDate,
	selectView,
} from "./selectors";

// utils
import get from "Utils/get";

// css
import css from "./Page";
import "react-datepicker/dist/react-datepicker.css";
import { getMyPermissions } from "../../../Api/services/me";
import { hasPermission } from "../../../Utils/accessControls";
import CompanyTaxAddress from "./Components/CompanyTaxAddress";

const dataExportOptions = [
	{ value: "employees", label: "Employees" },
	{ value: "goals", label: "Goals" },
];

const STAGINGENV = [
	{ value: "staging", label: "Staging" },
	{ value: "preview", label: "Preview" },
];

class CompanyPage extends React.Component {
	// Static Methods and Properties
	static propTypes = {
		lastSync: PropTypes.string,
		isCopyingData: PropTypes.bool.isRequired,
		isFunctionsRequesting: PropTypes.bool.isRequired,
		isDeletingIntegration: PropTypes.bool.isRequired,
		isResettingNotificationSettings: PropTypes.bool.isRequired,
		isUpdatingIntegration: PropTypes.bool.isRequired,
		initalized: PropTypes.bool.isRequired,
		init: PropTypes.func.isRequired,
		fetchPageData: PropTypes.func.isRequired,
		changeQuery: PropTypes.func.isRequired,
		sendIntegrationUpdate: PropTypes.func.isRequired,
		onIntegrationChange: PropTypes.func.isRequired,
		onOriginalIntegrationChange: PropTypes.func.isRequired,
		resetIntegration: PropTypes.func.isRequired,
		deleteIntegration: PropTypes.func.isRequired,
		environment: PropTypes.object.isRequired,
		activeView: PropTypes.string.isRequired,
		companyID: PropTypes.string.isRequired,
		currentQuery: PropTypes.string.isRequired,
		integration: PropTypes.string.isRequired,
		originalIntegration: PropTypes.string.isRequired,
		users: PropTypes.instanceOf(List).isRequired,
		company: PropTypes.instanceOf(Map).isRequired,
		resetNotificationDefaults: PropTypes.func.isRequired,
		copyStagingData: PropTypes.func.isRequired,
		runReportingEtls: PropTypes.func.isRequired,
		exportData: PropTypes.func.isRequired,
		userRoles: PropTypes.instanceOf(Array).isRequired,
		// I'm adding in this prop because it was missing, but I'm not sure it
		// is used at all.
		value: PropTypes.string,
		sendSurveyManually: PropTypes.func.isRequired,
		sendReminderEmailsManually: PropTypes.func.isRequired,
		isRequestingSendSurvey: PropTypes.bool,
		isRequestingSendReminderEmails: PropTypes.bool,
	};

	static defaultProps = {
		lastSync: null,
		value: null, // I'm adding in this prop because it was missing, but I'm not sure it is used at all.
	};

	// Lifecycle Methods
	constructor(props) {
		super(props);

		const asOfDate = moment(props.value);

		this.state = {
			value: props.value, // This appears to be unused, we should follow up on this later
			asOfDateValue: asOfDate.isValid() ? asOfDate.toDate() : null,
			etlDomain: null,
			displayChangeIntegrationModal: false,
		};
	}

	state = {
		exportValue: null,
		isControlKeyDown: false,
		isRequestingExport: false,
		isRequestingReportingEtl: false,
		copyToEnv: null,
		copyFromEnv: null,
		isRequestingSendSurvey: false,
		sendSurveyEnv: null,
		sendReminderEmailsEnv: null,
		isRequestingSendReminderEmails: false,
		confirmProductionEmailSend: false,
		permissions: [],
	};

	async componentDidMount() {
		this.props.init();
		window.addEventListener("keyup", this.handleKeyUp);
		window.addEventListener("keydown", this.handleKeyDown);
		this.setState({
			exportValue: dataExportOptions[0],
		});

		const permissions = await getMyPermissions({ env: this.props.environment.name });
		this.setState({ permissions });
	}

	componentDidUpdate = (prevProps) => {
		if (prevProps.environment !== this.props.environment) {
			this.props.fetchPageData();
		}
	};

	componentWillUnmount = () => {
		window.removeEventListener("keyup", this.handleKeyUp);
		window.removeEventListener("keydown", this.handleKeyDown);
	};

	// Custom Methods
	cancelDisplayChangeIntegrationModal = () => {
		this.setState({ displayChangeIntegrationModal: false });
	};

	handleKeyUp = (event) => {
		if (event.key.toLowerCase() === "control") {
			this.setState({ isControlKeyDown: false });
		}
	};

	handleKeyDown = (event) => {
		if (event.key.toLowerCase() === "control") {
			this.setState({ isControlKeyDown: true });
		}
	};

	handleChange = (value) => {
		this.props.changeQuery(value);
	};

	// When Integration value is changed in the dropdown
	handleIntegrationChange = (value) => {
		this.props.onIntegrationChange(value.value);
	};

	// When update button is clicked in Integration section
	handleIntegrationUpdateChange = () => {
		// No-op if we haven't changed the integration
		if (this.props.originalIntegration === this.props.integration) {
			return;
		}

		// If there is already a selected integration, display a modal that we
		// will delete the original integration.
		if (!["none", "disable"].includes(this.props.originalIntegration)) {
			this.setState({ displayChangeIntegrationModal: true });
		}
	};

	// When update button is confirmed in Integration section
	handleIntegrationUpdate = () => {
		// first close the modal
		this.setState({ displayChangeIntegrationModal: false });

		// If there is already a selected integration, delete the original
		// integration.
		if (!["none", "disable"].includes(this.props.originalIntegration)) {
			// First Delete the old integration
			this.props.resetIntegration();
			this.props.deleteIntegration();
		}

		// Now go ahead and create the new integration
		this.props.sendIntegrationUpdate();

		// Finally, the new "Original" integration is now the current
		// integration
		this.props.onOriginalIntegrationChange(this.props.integration);
	};

	handleDeleteIntegration = () => {
		this.props.deleteIntegration();
	};

	handleResetIntegration = () => {
		this.props.resetIntegration();
	};

	handleResetNotificationDefaults = () => {
		this.props.resetNotificationDefaults();
	};

	handleDateChange = (newValue) => {
		this.setState({ value: newValue, asOfDateValue: newValue });
	};

	handleETLDomainChange = (newValue) => {
		this.setState({ etlDomain: newValue });
	};

	handelCopyData = () => {
		const { copyFromEnv, copyToEnv } = this.state;
		this.props.copyStagingData({ fromEnv: copyFromEnv.value, toEnv: copyToEnv.value });
	};

	handleRunReportingETL = async () => {
		this.setState({ isRequestingReportingEtl: true });
		const { etlDomain } = this.state;
		await this.props.runReportingEtls(etlDomain.value);
		this.setState({ isRequestingReportingEtl: false });
	};

	handleExportValueChange = (value) => {
		this.setState({ exportValue: value });
	};

	handleExportData = () => {
		this.setState({ isRequestingExport: true });

		const { exportValue } = this.state;
		const exportID = get(["value"], exportValue, null);

		this.props.exportData(exportID);

		setTimeout(() => {
			this.setState({ isRequestingExport: false });
		}, 5000);
	};

	handleSendSurveyEnvChange = (value) => {
		this.setState({ sendSurveyEnv: value });
	};

	handleSendSurvey = async () => {
		this.setState({ isRequestingSendSurvey: true });
		const { sendSurveyEnv } = this.state;
		const stagingEnv = get(["value"], sendSurveyEnv, null);
		await this.props.sendSurveyManually(stagingEnv);
		this.setState({ isRequestingSendSurvey: false });

		setTimeout(() => {}, 5000);
	};

	handleSendReminderEmailsEnvChange = (value) => {
		this.setState({ sendReminderEmailsEnv: value });
		this.setState({ confirmProductionEmailSend: false });
	};

	handleSendReminderEmails = async () => {
		this.setState({ isRequestingSendReminderEmails: true });
		const { sendReminderEmailsEnv } = this.state;
		const stagingEnv = get(["value"], sendReminderEmailsEnv, null);
		this.setState({ confirmProductionEmailSend: false });
		await this.props.sendReminderEmailsManually(stagingEnv);
		this.setState({ isRequestingSendReminderEmails: false });

		setTimeout(() => {}, 5000);
	};

	handlePromptForConfirmation = () => {
		this.setState({ confirmProductionEmailSend: true });
	};

	handleFromEnv = (value) => {
		this.setState({ copyFromEnv: value });
	};

	handleToEnv = (value) => {
		this.setState({ copyToEnv: value });
	};

	// Render Method
	render = () => {
		const {
			exportValue,
			isControlKeyDown,
			isRequestingExport,
			isRequestingReportingEtl,
			displayChangeIntegrationModal,
			copyFromEnv,
			copyToEnv,
			sendSurveyEnv,
			sendReminderEmailsEnv,
			confirmProductionEmailSend,
			permissions,
		} = this.state;

		const {
			users,
			company,
			lastSync,
			userRoles,
			companyID,
			activeView,
			initalized,
			integration,
			currentQuery,
			isCopyingData,
			isFunctionsRequesting,
			isDeletingIntegration,
			isResettingNotificationSettings,
			isUpdatingIntegration,
			isRequestingSendSurvey,
			isRequestingSendReminderEmails,
		} = this.props;

		const primaryView = activeView.view;
		const secondaryView = activeView.viewId;

		const integrationOptions = [
			{ value: "disabled", label: "None" },
			{ value: "adp-vantage", label: "ADP Vantage" },
			{ value: "adp-wfn", label: "ADP Workforce Now" },
			{ value: "bamboohr", label: "BambooHR" },
			{ value: "gusto", label: "Gusto" },
			{ value: "netchex", label: "Netchex" },
			{ value: "rippling", label: "Rippling" },
			{ value: "ukg-pro", label: "UKG Pro" },
		];

		const reportingETLDomains = [
			{ value: "employee", label: "Employee Data" },
			{ value: "review", label: "Review Data" },
			{ value: "goal", label: "Goal Data" },
			{ value: "all", label: "Run All Domains" },
		];

		const integrationValue = integrationOptions.find((item) => item.value === integration) || integrationOptions[0];

		const etlDomain = this.state.etlDomain || null;

		const changeHRIntegrationModalContent = (
			<div>
				<p>
					Are you sure you want to change the integration? This will reset and delete all data associated with
					the current integration.
				</p>
			</div>
		);
		const changeHRIntegrationModalButtons = (
			<div>
				<div className={css.cancelConfirmModalButtons}>
					<div className={css.modalButton}>
						<Button version={2} color="green" onClick={this.handleIntegrationUpdate}>
							Yes
						</Button>
					</div>
					<div className={css.modalButton}>
						<Button version={2} color="red" onClick={() => this.cancelDisplayChangeIntegrationModal()}>
							Close
						</Button>
					</div>
				</div>
			</div>
		);

		if (initalized && !company) {
			return <div />;
		}
		if (company.size === 0) {
			return <Spinner />;
		}

		if (!permissions) {
			return null;
		}

		// helper variables for new send survey and send reminder email functionality
		const loganEnv = this.props.environment.name;
		const engagementEnabled = company.getIn(["functions", "engagement"], false) === "enabled";

		const canSendSurvey = hasPermission(permissions ?? [], "send:engagement-survey");
		const displayManualSendSurvey = engagementEnabled && canSendSurvey;

		const canSendReminderEmails = hasPermission(permissions ?? [], "send:engagement-reminder-email");

		const displayManualSendReminderEmails = engagementEnabled && canSendReminderEmails;

		let sendReminderEmailEnvOptions = [];
		if (loganEnv === "staging") {
			sendReminderEmailEnvOptions = STAGINGENV;
		} else if (loganEnv === "production") {
			sendReminderEmailEnvOptions = [{ value: "production", label: "Production" }];
		}

		// check permissions for bulk change emails functionality
		const canBulkChangeEmails = hasPermission(permissions ?? [], "update:bulk-emails");

		const renderBulkEmailChange = () => {
			if (canBulkChangeEmails) {
				return (
					<div className={css.bulkEmailChangeContainer}>
						<BulkEmailChange companyID={companyID} />
					</div>
				);
			} else {
				return (
					<div className={css.noPermissionsMessage}>
						Sorry, you do not have permission to bulk update emails.
					</div>
				);
			}
		};

		return (
			<div className={css.CompanyWrapper}>
				{displayChangeIntegrationModal && (
					<Modal
						content={changeHRIntegrationModalContent}
						buttons={changeHRIntegrationModalButtons}
						handleClose={() => this.cancelDisplayChangeIntegrationModal(false)}
					/>
				)}
				<div className={css.companyContainer}>
					<div className={css.mainContent}>
						<div>
							<div className={css.companyCard}>
								<div>
									<div className={`${css.section}`}>
										<div>
											<div
												className={css.brandingContainer}
												style={{ backgroundColor: company.getIn(["nav_color"]) }}
											>
												<CompanyLogo className={css.logo} companyID={companyID} />
											</div>
										</div>
									</div>

									<div className={`${css.section}`}>
										<div>
											<div className={css.companyName}>{company.get("name")}</div>
										</div>
									</div>

									<div className={`${css.section}`}>
										<div>
											<Button
												href={`${this.props.environment.host}/god-admin/setup/${companyID}`}
												external={true}
												className={css.viewInPerformYard}
											>
												View in PerformYard
											</Button>
										</div>
									</div>

									<div className={css.section}>
										<div>
											<div className={css.companySettingsContainer}>
												<CompanySetting
													label="Name"
													name="name"
													value={company.get("name", "")}
													type="text"
												/>
												<CompanySetting
													label="Max Users"
													name="maxUsers"
													value={company.get("max_users", 0)}
													type="number"
												/>
												<CompanySetting
													label="Implementation Date"
													name="implementationDate"
													value={company.get("implementation_date", "")}
													type="date"
												/>
												<CompanySetting
													label="Expiration"
													name="expires"
													value={company.get("expires", "")}
													type="date"
												/>
												<CompanySetting
													label="Disallow Surveys"
													name="disallowSurveys"
													value={company.get("disallow_surveys", false)}
													type="boolean"
												/>
												<CompanySetting
													label="Netchex Company Codes"
													name="netchexCodes"
													value={company.get("netchex_codes", "")}
													type="text"
												/>
												<CompanySetting
													label="Salesforce ID"
													name="salesforceID"
													value={company.getIn(["loganProps", "salesforceID"], "")}
													type="text"
												/>
											</div>
										</div>
									</div>

									<div className={css.section}>
										<div>
											<div className={css.sectionLabel}>Company Tax Info</div>

											<div className={css.companySettingsContainer}>
												<CompanyTaxAddress data={company.get("tax_address", Map())} />
											</div>
										</div>
									</div>

									<div className={`${css.section} ${css.integrationForm}`}>
										<div>
											<div className={css.sectionLabel}>Integration</div>

											<div className={css.integrationInputs}>
												<Select
													value={integrationValue}
													onChange={this.handleIntegrationChange}
													options={integrationOptions}
												/>
												<Button
													isRequesting={isFunctionsRequesting}
													onClick={this.handleIntegrationUpdateChange}
													className={css.updateButton}
												>
													Update
												</Button>
											</div>

											{lastSync ? (
												<div className={css.lastSync}>
													Last Sync: {moment(lastSync).calendar()}
												</div>
											) : null}

											<div style={{ marginTop: 20 }}>
												<p style={{ marginBottom: 5 }}>
													To click these, hold down the control key:
												</p>
												<div className={css.integrationButtons}>
													<Button
														disabled={!isControlKeyDown}
														color={isControlKeyDown ? "red" : "grey"}
														isRequesting={isDeletingIntegration}
														onClick={this.handleDeleteIntegration}
														className={css.deleteIntegrationButton}
													>
														Delete Integration
													</Button>
													<Button
														disabled={!isControlKeyDown}
														color={isControlKeyDown ? "red" : "grey"}
														isRequesting={isUpdatingIntegration}
														onClick={this.handleResetIntegration}
														className={css.updateIntegrationButton}
													>
														Reset Integration
													</Button>
												</div>
											</div>
										</div>
									</div>
									{userRoles.includes("notifications-admin") ? (
										<div className={`${css.section}`}>
											<div>
												<div className={css.sectionLabel}>Reset Notification Defaults</div>

												<div style={{ marginTop: 20 }}>
													<p style={{ marginBottom: 5 }}>
														To click, hold down the control key:
													</p>

													<div className={css.integrationButtons}>
														<Button
															disabled={!isControlKeyDown}
															color={isControlKeyDown ? "red" : "grey"}
															isRequesting={isResettingNotificationSettings}
															onClick={this.handleResetNotificationDefaults}
															className={css.deleteIntegrationButton}
														>
															Copy Notifications to all Employees
														</Button>
													</div>
												</div>
											</div>
										</div>
									) : null}

									{userRoles.includes("engineering") ? (
										<div className={`${css.section} ${css.integrationForm}`}>
											<div>
												<div className={css.sectionLabel}>Reporting ETL</div>
												<p style={{ marginBottom: 5 }}>To click, hold down the control key:</p>
												<div className={css.integrationInputs}>
													<Select
														value={etlDomain}
														onChange={this.handleETLDomainChange}
														options={reportingETLDomains}
													/>
													<Button
														disabled={
															!isControlKeyDown || !etlDomain || isRequestingReportingEtl
														}
														color={isControlKeyDown && etlDomain ? "red" : "grey"}
														isRequesting={isRequestingReportingEtl}
														onClick={this.handleRunReportingETL}
														className={css.updateButton}
													>
														Run ETL
													</Button>
												</div>
											</div>
										</div>
									) : null}

									{userRoles.includes("engineering") && this.props.environment.name === "staging" ? (
										<div className={`${css.section} ${css.integrationForm}`}>
											<div className={css.sectionLabel}>Copy Data</div>

											<div style={{ marginTop: 5 }}>
												<p style={{ marginBottom: 5 }}>To click, hold down the control key:</p>

												<div style={{ marginBottom: 10 }} className={css.integrationButtons}>
													{/* From - To */}
													<div className={css.inputsContainer}>
														<p style={{ marginBottom: 2, marginLeft: 2 }}>From:</p>
														<Select
															value={copyFromEnv}
															options={STAGINGENV}
															onChange={this.handleFromEnv}
														/>
													</div>
													<div className={css.inputsContainer}>
														<p style={{ marginBottom: 2, marginLeft: 2 }}>To:</p>
														<Select
															value={copyToEnv}
															options={STAGINGENV}
															onChange={this.handleToEnv}
															label="To Environment"
														/>
													</div>
												</div>
												<Button
													disabled={!isControlKeyDown}
													color={isControlKeyDown ? "red" : "grey"}
													isRequesting={isCopyingData}
													onClick={this.handelCopyData}
													className={css.button}
												>
													Copy Company
												</Button>
											</div>
										</div>
									) : null}

									{exportValue ? (
										<div className={`${css.section} ${css.integrationForm}`}>
											<div>
												<div className={css.sectionLabel}>Data Export</div>

												<div className={css.integrationInputs}>
													<Select
														value={exportValue}
														options={dataExportOptions}
														onChange={this.handleExportValueChange}
													/>
													<Button
														isRequesting={isRequestingExport}
														onClick={this.handleExportData}
														className={css.updateButton}
													>
														Export CSV
													</Button>
												</div>
											</div>
										</div>
									) : null}
									{displayManualSendSurvey ? (
										<div className={`${css.section} ${css.integrationForm}`}>
											<div className={css.sectionLabel}>Trigger Survey</div>

											<div className={css.integrationInputs}>
												<Select
													value={sendSurveyEnv}
													options={STAGINGENV}
													onChange={this.handleSendSurveyEnvChange}
												/>
												<Button
													isRequesting={isRequestingSendSurvey}
													onClick={this.handleSendSurvey}
													className={css.updateButton}
												>
													Send Survey
												</Button>
											</div>
										</div>
									) : null}
									{displayManualSendReminderEmails ? (
										<div className={`${css.section} ${css.integrationForm}`}>
											<div>
												<div className={css.sectionLabel}>Send Reminder Emails</div>

												<div className={css.integrationInputs}>
													<Select
														value={sendReminderEmailsEnv}
														options={sendReminderEmailEnvOptions}
														onChange={this.handleSendReminderEmailsEnvChange}
													/>
													{confirmProductionEmailSend ? (
														<Button
															onClick={this.handleSendReminderEmails}
															className={css.updateButton}
															color="red"
														>
															Confirm
														</Button>
													) : (
														<Button
															isRequesting={isRequestingSendReminderEmails}
															onClick={
																sendReminderEmailsEnv?.value === "production"
																	? this.handlePromptForConfirmation
																	: this.handleSendReminderEmails
															}
															className={css.updateButton}
														>
															Send Reminder Emails
														</Button>
													)}
												</div>
											</div>
										</div>
									) : null}
								</div>
							</div>
						</div>

						<div>
							<div className={css.navigationContainer}>
								<CompanyNavigation />
							</div>

							{primaryView === "snapshot" ? (
								<div className={css.companySnapshotContainer}>
									<CompanySnapshot companyID={companyID} />
								</div>
							) : null}

							{primaryView === "feature-flags" && !secondaryView ? (
								<div className={css.companySnapshotContainer}>
									<CompanyFeatureFlags companyID={companyID} />
								</div>
							) : null}

							{primaryView === "feature-flags" && secondaryView ? (
								<div className={css.companySnapshotContainer}>
									<EditFeatureFlag companyID={companyID} />
								</div>
							) : null}

							{primaryView === "employees" ? (
								<div className={css.companyUsers}>
									<div>
										<TextInput
											placeholder="Search employees..."
											value={currentQuery}
											onChange={this.handleChange}
										/>
									</div>

									<div className={css.userTableHead}>
										<div>Name</div>
										<div>Status</div>
										<div>Invited (Last Invited Date)</div>
										<div className={css.alignRight}>Last Seen</div>
									</div>

									<div className={css.usersListContainer}>
										{users.map((user) => (
											<User key={user.get("id")} className={css.userItem} user={user} />
										))}
									</div>
								</div>
							) : null}

							{primaryView === "invoices" ? (
								<div className={css.companySnapshotContainer}>
									<CompanyInvoice companyID={companyID} />
								</div>
							) : null}

							{primaryView === "deleted-objects" ? (
								<div className={css.companySnapshotContainer}>
									<DeletedObjects companyID={companyID} />
								</div>
							) : null}

							{primaryView === "access-controls" ? (
								<div className={css.companySnapshotContainer}>
									<AccessControls companyID={companyID} />
								</div>
							) : null}
							{primaryView === "bulk-email-change" ? renderBulkEmailChange() : null}

							{primaryView === "engagement-settings-logs" ? (
								<div className={css.companySnapshotContainer}>
									<EngagementLogs companyID={companyID} />
								</div>
							) : null}
						</div>
					</div>

					<div className={css.dataDump}>
						<div className={css.prettyJSON}>{JSON.stringify(company.toJS(), null, 2)}</div>
					</div>
				</div>
			</div>
		);
	};
}

const mapStateToProps = (state) => ({
	data: getData(state),
	activeView: selectView(state),
	company: getCompany(state),
	pageSize: getPageSize(state),
	pageLimit: getPageLimit(state),
	companyID: getCompanyID(state),
	currentPage: getCurrentPage(state),
	users: getCompanyUsers(state),
	currentQuery: getCurrentQuery(state),
	integration: getIntegrationValue(state),
	originalIntegration: getOriginalIntegrationValue(state),
	lastSync: getLastSyncDate(state),
	environment: getCurrentEnvironment(state),
	userRoles: getUserRoles(state),
	isCopyingData: getIsRequesting(state, "COMPANIES:COPY.STAGE"),
	isFunctionsRequesting: getIsRequesting(state, "COMPANIES:SETINTEGRATION"),
	isDeletingIntegration: getIsRequesting(state, "COMPANIES:INTEGRATION.DELETE"),
	isUpdatingIntegration: getIsRequesting(state, "COMPANIES:INTEGRATION.UPDATE"),
	isResettingNotificationSettings: getIsRequesting(state, "COMPANIES:RESET.NOTIFICATIONS"),
	initalized: getInitalized(state),
	userIsAdmin: getUserIsAdmin(state),
	isRequestingSendSurvey: getIsRequesting(state, "COMPANIES:SEND.SURVEY.MANUALLY"),
	isRequestingSendReminderEmails: getIsRequesting(state, "COMPANIES:SEND.REMINDER_EMAILS.MANUALLY"),
});

const mapDispatchToProps = (dispatch) => ({
	fetchPageData: () => dispatch(fetchPageData()),
	resetIntegration: () => dispatch(resetIntegration()),
	resetNotificationDefaults: () => dispatch(resetNotificationDefaults()),
	copyStagingData: ({ toEnv, fromEnv }) => dispatch(copyStagingData({ toEnv, fromEnv })),
	runReportingEtls: (domain) => dispatch(runReportingEtls(domain)),
	deleteIntegration: () => dispatch(deleteIntegration()),
	changeQuery: (query) => dispatch(changeQuery(query)),
	init: (graphWidth) => dispatch(init(graphWidth)),
	sendIntegrationUpdate: () => dispatch(sendIntegrationUpdate()),
	exportData: (dataType) => dispatch(exportData(dataType)),
	onIntegrationChange: (value) => dispatch(updateUI(["company", "functions", "hr-integration"], value)),
	onOriginalIntegrationChange: (value) =>
		dispatch(updateUI(["originalCompany", "functions", "hr-integration"], value)),
	sendSurveyManually: (companyID, sendSurveyEnv) => dispatch(sendSurveyManually(companyID, sendSurveyEnv)),
	sendReminderEmailsManually: (companyID, sendReminderEmailsEnv) =>
		dispatch(sendReminderEmailsManually(companyID, sendReminderEmailsEnv)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyPage);
